import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// v2: the old key was polluted — the agenda used to persist a global collapse on
// every visit, so almost everyone had a stale '1' that wrongly kept the dashboard
// collapsed. Bumping the key resets everyone to the new "default per route" model.
const STORAGE_KEY = 'agora-rail-collapsed-v2';
const BROADCAST_EVENT = 'agora-rail-collapse-change';

// Routes with no per-route collapse behavior at all — reserved for a future
// surface that truly must never show the toggle (e.g. an editor that hijacks
// the whole viewport). Empty today: every editor below only *defaults* to
// collapsed so the user can still open the rail with the arrow.
const FORCE_COLLAPSE_PATTERNS: RegExp[] = [];

// Routes that merely *default* to collapsed for breathing room. The rail's
// own inline sidebar (agenda's calendar, or an asset editor's step nav) owns
// the left side of the viewport at first, but the toggle stays visible so the
// user can still expand the rail — and unlike a persisted preference it never
// leaks into other views (e.g. the dashboard stays expanded). Only applies
// when the user hasn't made an explicit choice yet.
const DEFAULT_COLLAPSE_PATTERNS: RegExp[] = [
  /^\/agenda(\/|$)/,
  // Conversaciones: bandeja + hilo + ficha del cliente necesitan el ancho, como la agenda.
  /^\/conversaciones(\/|$)/,
  /^\/mi-pagina/,
  /^\/servicio\/.*\/editar/,
  /^\/servicio\/crear/,
  /^\/eventos\/crear/,
  /^\/eventos\/.*\/(editar|info)/,
  /^\/curso-online\/(crear|.*\/editar)/,
  /^\/descargable\/(crear|.*\/editar)/,
  /^\/planesmensuales\/(crear|.*\/editar)/
];

// null = the user hasn't toggled the rail yet → the per-route default decides.
const readInitial = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return null;
  } catch {
    return null;
  }
};

// How long the width transition stays enabled after an explicit toggle. Route-driven
// collapses (entering /agenda, an editor…) switch width instantly: animating them made
// the whole content column reflow for 260ms on every navigation (and the agenda resize
// its calendar every frame).
const TOGGLE_ANIMATION_MS = 320;

export const useRailCollapsed = () => {
  const [userPref, setUserPref] = useState<boolean | null>(readInitial);
  const [animate, setAnimate] = useState(false);
  const animateTimer = useRef<number | undefined>(undefined);
  const { pathname } = useLocation();

  const forced = FORCE_COLLAPSE_PATTERNS.some((re) => re.test(pathname));
  const defaultCollapsed = DEFAULT_COLLAPSE_PATTERNS.some((re) => re.test(pathname));
  // Explicit user choice wins; otherwise fall back to the per-route default.
  const collapsed = forced || (userPref ?? defaultCollapsed);

  useEffect(() => {
    const onSync = (event: Event) => {
      const detail = (event as CustomEvent<{ userPref: boolean }>).detail;
      if (detail && typeof detail.userPref === 'boolean') {
        setUserPref(detail.userPref);
      }
    };
    window.addEventListener(BROADCAST_EVENT, onSync);
    return () => window.removeEventListener(BROADCAST_EVENT, onSync);
  }, []);

  const applyPref = useCallback((next: boolean) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      // ignore
    }
    window.dispatchEvent(new CustomEvent(BROADCAST_EVENT, { detail: { userPref: next } }));
    window.clearTimeout(animateTimer.current);
    setAnimate(true);
    animateTimer.current = window.setTimeout(() => setAnimate(false), TOGGLE_ANIMATION_MS);
    setUserPref(next);
  }, []);

  useEffect(() => () => window.clearTimeout(animateTimer.current), []);

  const toggle = useCallback(() => {
    if (forced) return;
    applyPref(!collapsed);
  }, [forced, collapsed, applyPref]);

  const expand = useCallback(() => {
    if (forced) return;
    applyPref(false);
  }, [forced, applyPref]);

  return { collapsed, toggle, expand, forced, animate };
};
